import { useEffect, useState } from "react";
import { api, apiError, eur, itDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { AlertTriangle, ArrowDownUp, Boxes, Send } from "lucide-react";

export default function Magazzino() {
  const [products, setProducts] = useState([]);
  const [moves, setMoves] = useState([]);
  const [adjustFor, setAdjustFor] = useState(null);
  const [reorder, setReorder] = useState({ items: [], total_cost: 0 });
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const sendReorder = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/stock/reorder/send");
      setMsg(data.sent
        ? `Proposta d'ordine inviata a ${data.to} con ${data.items} articoli.`
        : data.message);
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const load = () => {
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/stock/movements").then(({ data }) => setMoves(data));
    api.get("/stock/reorder").then(({ data }) => setReorder(data));
  };
  useEffect(() => { load(); }, []);

  const stockables = products.filter((p) => p.kind !== "servizio");
  const low = stockables.filter((p) => (p.stock || 0) <= (p.min_stock ?? 5));

  const adjust = async (values) => {
    try {
      await api.post("/stock/adjust", { product_id: adjustFor.id, quantity: Number(values.quantity), note: values.note });
      load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="magazzino-page">
      <PageHeader title="Magazzino ricambi" subtitle={`${stockables.length} articoli a giacenza`}>
        <Button variant="outline" className="h-11 rounded-sm" onClick={sendReorder} disabled={sending}
                data-testid="send-reorder-btn">
          <Send className="h-4 w-4 mr-2" /> {sending ? "Invio…" : "Invia proposta d'ordine"}
        </Button>
      </PageHeader>

      {msg && <p className="mb-4 text-sm font-medium text-primary" data-testid="reorder-message">{msg}</p>}

      {reorder.items?.length > 0 && (
        <Card className="rounded-sm shadow-none mb-8 border-destructive/40" data-testid="reorder-card">
          <div className="p-6 border-b border-border">
            <p className="overline">Proposta d'ordine fornitore</p>
            <h3 className="font-display text-xl font-bold tracking-tight mt-1">
              {reorder.items.length} articoli da riordinare · costo stimato {eur(reorder.total_cost)}
            </h3>
          </div>
          <div className="divide-y divide-border">
            {reorder.items.map((i) => (
              <div key={i.product_id} className="px-6 py-3 flex items-center justify-between gap-3"
                   data-testid={`reorder-item-${i.product_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">
                    <span className="font-mono text-xs text-muted-foreground mr-2">{i.code}</span>{i.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Giacenza {i.stock} / soglia {i.min_stock} · fornitore {i.supplier}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display text-xl font-black tracking-tighter">{i.suggested_qty}</p>
                  <p className="text-xs text-muted-foreground">{eur(i.estimated_cost)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">Articoli</p>
          <p className="font-display text-4xl font-black tracking-tighter mt-2">{stockables.length}</p>
        </Card>
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">Sotto soglia</p>
          <p className="font-display text-4xl font-black tracking-tighter mt-2 text-destructive"
             data-testid="low-stock-count">{low.length}</p>
        </Card>
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">Valore magazzino</p>
          <p className="font-display text-4xl font-black tracking-tighter mt-2">
            {eur(stockables.reduce((s, p) => s + (p.stock || 0) * (p.cost || 0), 0))}
          </p>
        </Card>
      </div>

      <h3 className="font-display text-xl font-bold tracking-tight mb-3">Giacenze</h3>
      <ListCard testid="giacenze-list">
        {stockables.map((p) => {
          const alert = (p.stock || 0) <= (p.min_stock ?? 5);
          return (
            <div key={p.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-muted-foreground">{p.code}</span>
                  <p className="font-semibold">{p.name}</p>
                  <Badge variant="secondary" className="rounded-sm capitalize">{p.kind}</Badge>
                  {alert && (
                    <Badge className="rounded-sm bg-destructive text-destructive-foreground">
                      <AlertTriangle className="h-3 w-3 mr-1" /> da riordinare
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Soglia minima {p.min_stock ?? 5} {p.unit} · costo {eur(p.cost)}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <span className={`font-display text-2xl font-black tracking-tighter ${alert ? "text-destructive" : ""}`}
                      data-testid={`stock-${p.id}`}>
                  {p.stock} <span className="text-xs font-normal text-muted-foreground">{p.unit}</span>
                </span>
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setAdjustFor(p)}
                        data-testid={`adjust-stock-${p.id}`}>
                  <ArrowDownUp className="h-3.5 w-3.5 mr-2" /> Rettifica
                </Button>
              </div>
            </div>
          );
        })}
        {stockables.length === 0 && <Empty text="Nessun articolo a magazzino." />}
      </ListCard>

      <h3 className="font-display text-xl font-bold tracking-tight mt-10 mb-3">Movimenti recenti</h3>
      <ListCard testid="movimenti-list">
        {moves.map((m) => (
          <div key={m.id} className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">
                <span className="font-mono text-xs text-muted-foreground mr-2">{m.product_code}</span>
                {m.product_name}
              </p>
              <p className="text-xs text-muted-foreground">
                {m.reason}{m.intervention_code ? ` · ${m.intervention_code}` : ""} · {itDate(m.created_at)}
              </p>
            </div>
            <span className={`font-mono text-sm font-semibold ${m.quantity < 0 ? "text-destructive" : "text-emerald-600"}`}>
              {m.quantity > 0 ? "+" : ""}{m.quantity}
            </span>
          </div>
        ))}
        {moves.length === 0 && (
          <Empty text="Nessun movimento: le giacenze si scalano quando i tecnici usano i ricambi negli interventi." />
        )}
      </ListCard>

      <FormDialog
        open={!!adjustFor}
        onOpenChange={(v) => !v && setAdjustFor(null)}
        title={`Rettifica giacenza — ${adjustFor?.name || ""}`}
        fields={[
          { name: "quantity", label: "Quantità (+ carico / − scarico)", type: "number", required: true },
          { name: "note", label: "Motivo", full: true },
        ]}
        initial={{ quantity: 0 }}
        onSubmit={adjust}
        testid="adjust-dialog"
      />
    </div>
  );
}
